// Pure helpers for a Location's property details (address, floor area,
// occupancy, EPC ...): which fields each flavour sees, validation, and the
// mapping between the API's camelCase Location and the form's string values.
// Shared by LocationDetailsForm (Property Hub, Locations "Edit details") and
// the quick-add dialog. The backend (Location.clean()) stays authoritative.

export const PROPERTY_KIND_OPTIONS = [
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'mixed', label: 'Mixed use' },
    { value: 'block', label: 'Block' },
    { value: 'outbuilding', label: 'Outbuilding' },
    { value: 'other', label: 'Other' }
]

export const EPC_RATINGS = ['A', 'B', 'C', 'D', 'E', 'F', 'G']

const BASE_FIELDS = ['addressLine', 'postcode', 'floorAreaM2', 'occupancy']

// A landlord's or operator's facts (kind, bedrooms, EPC) only for the flavours
// that let or run property; a school, university or home sees the base set.
const PROPERTY_FIELDS = [
    ...BASE_FIELDS,
    'bedrooms',
    'propertyKind',
    'epcRating',
    'epcExpiry'
]

export const PROPERTY_FIELDS_BY_FLAVOUR = {
    school: BASE_FIELDS,
    university: BASE_FIELDS,
    estate: PROPERTY_FIELDS,
    business: PROPERTY_FIELDS,
    home: BASE_FIELDS
}

export const ALL_DETAIL_FIELDS = PROPERTY_FIELDS

export function fieldsForFlavour(flavour) {
    return PROPERTY_FIELDS_BY_FLAVOUR[flavour] || BASE_FIELDS
}

const NUMERIC_FIELDS = ['floorAreaM2', 'occupancy', 'bedrooms']
const NULLABLE_FIELDS = [...NUMERIC_FIELDS, 'epcExpiry']

// Upper-case and re-space as "OUTWARD INWARD" (the last three characters are
// the inward code), matching the backend's normalise_postcode().
export function normalisePostcode(value) {
    const compact = String(value ?? '')
        .replace(/\s+/g, '')
        .toUpperCase()
    if (compact.length <= 3) {
        return compact
    }
    return `${compact.slice(0, -3)} ${compact.slice(-3)}`
}

// Permissive UK pattern, matched against the normalised form (same as the backend).
const UK_POSTCODE = /^[A-Z]{1,2}[0-9][A-Z0-9]? [0-9][A-Z]{2}$/

export function isValidPostcode(value) {
    return UK_POSTCODE.test(value ?? '')
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

function isValidIsoDate(value) {
    if (!ISO_DATE.test(value)) {
        return false
    }
    const date = new Date(`${value}T00:00:00Z`)
    return (
        !Number.isNaN(date.getTime()) &&
        date.toISOString().slice(0, 10) === value
    )
}

// API Location -> form values (every value a string, '' when unset).
export function fromLocation(location) {
    const values = {}
    for (const field of ALL_DETAIL_FIELDS) {
        const value = location?.[field]
        values[field] =
            value === null || value === undefined ? '' : String(value)
    }
    return values
}

/**
 * Field-scoped validation of the form values for the given fields. Returns
 * `{ field: message }`; empty when valid. Blank is always valid (every field
 * is optional).
 */
export function validateLocationDetails(values, fields = ALL_DETAIL_FIELDS) {
    const errors = {}
    const value = (field) => String(values?.[field] ?? '').trim()

    if (fields.includes('floorAreaM2') && value('floorAreaM2') !== '') {
        const text = value('floorAreaM2')
        const number = Number(text)
        if (!/^\d+(\.\d{1,2})?$/.test(text) || !Number.isFinite(number)) {
            errors.floorAreaM2 =
                'Enter the floor area as a number of square metres, with up to 2 decimal places.'
        } else if (number >= 1e8) {
            errors.floorAreaM2 = 'That floor area is too large.'
        }
    }
    for (const field of ['occupancy', 'bedrooms']) {
        if (fields.includes(field) && value(field) !== '') {
            if (!/^\d+$/.test(value(field))) {
                errors[field] = 'Enter a whole number, 0 or more.'
            } else if (field === 'bedrooms' && Number(value(field)) > 32767) {
                errors[field] = 'That is too many bedrooms.'
            }
        }
    }
    if (fields.includes('postcode') && value('postcode') !== '') {
        if (!isValidPostcode(normalisePostcode(value('postcode')))) {
            errors.postcode = 'Enter a valid UK postcode, e.g. EH4 1QX.'
        }
    }
    if (fields.includes('addressLine') && value('addressLine').length > 255) {
        errors.addressLine = 'Keep the address to 255 characters.'
    }
    if (
        fields.includes('propertyKind') &&
        value('propertyKind') !== '' &&
        !PROPERTY_KIND_OPTIONS.some(
            (option) => option.value === value('propertyKind').toLowerCase()
        )
    ) {
        errors.propertyKind = 'Choose a property kind from the list.'
    }
    if (
        fields.includes('epcRating') &&
        value('epcRating') !== '' &&
        !EPC_RATINGS.includes(value('epcRating').toUpperCase())
    ) {
        errors.epcRating = 'The EPC rating must be A to G.'
    }
    if (
        fields.includes('epcExpiry') &&
        value('epcExpiry') !== '' &&
        !isValidIsoDate(value('epcExpiry'))
    ) {
        errors.epcExpiry = 'Enter the expiry date as YYYY-MM-DD.'
    }
    return errors
}

/**
 * Form values -> PATCH body (camelCase), for the given fields only. Blank
 * numbers and dates become `null` and blank text `''`, so saving a cleared
 * field clears it on the server. Assumes `validateLocationDetails` passed.
 */
export function toPatchBody(values, fields = ALL_DETAIL_FIELDS) {
    const body = {}
    for (const field of fields) {
        const text = String(values?.[field] ?? '').trim()
        if (NULLABLE_FIELDS.includes(field)) {
            if (text === '') {
                body[field] = null
            } else if (NUMERIC_FIELDS.includes(field)) {
                body[field] = Number(text)
            } else {
                body[field] = text
            }
        } else if (field === 'postcode') {
            body[field] = text === '' ? '' : normalisePostcode(text)
        } else if (field === 'propertyKind') {
            body[field] = text.toLowerCase()
        } else if (field === 'epcRating') {
            body[field] = text.toUpperCase()
        } else {
            body[field] = text
        }
    }
    return body
}

/**
 * A plausibility hint (never a block) for a residential property whose area
 * per bedroom is outside 10-200 m², which usually means square feet were
 * entered as square metres or the wrong unit count. `null` when nothing to say.
 */
export function areaPerBedroomWarning(values) {
    if (String(values?.propertyKind ?? '').toLowerCase() !== 'residential') {
        return null
    }
    const area = Number(values?.floorAreaM2)
    const bedrooms = Number(values?.bedrooms)
    if (
        !values?.floorAreaM2 ||
        !values?.bedrooms ||
        !Number.isFinite(area) ||
        !Number.isFinite(bedrooms) ||
        area <= 0 ||
        bedrooms <= 0
    ) {
        return null
    }
    const perBedroom = area / bedrooms
    if (perBedroom < 10 || perBedroom > 200) {
        return `That is ${Math.round(perBedroom)} m² per bedroom, which is unusual for a home. Check the floor area is in square metres, not square feet.`
    }
    return null
}

// Display helpers shared by the peek and the hub summary.
export function formatFloorArea(value) {
    if (value === null || value === undefined || value === '') {
        return null
    }
    const number = Number(value)
    if (!Number.isFinite(number)) {
        return null
    }
    return `${number.toLocaleString('en-GB', { maximumFractionDigits: 2 })} m²`
}

export function propertyKindLabel(value) {
    return (
        PROPERTY_KIND_OPTIONS.find((option) => option.value === value)?.label ??
        null
    )
}

// The occupancy label, worded with the org's own term for the people a
// location is normalised by (t comes from useTerminology).
export function occupancyLabel(t) {
    const people = t('pupils')
    return `Number of ${people}`
}
